import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import { questionService } from '../../services/questionService';
import '../../assets/css/Research.css';

const ProgramAdminViewQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const QUESTIONS_PER_PAGE = 15;
  const location = useLocation();

  useEffect(() => {
    loadQuestions();

    // Check for success message from redirect (e.g., after editing)
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setShowSuccess(true);

      // Clear the message from location state after displaying
      window.history.replaceState({}, document.title);

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    }
  }, [location]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      // Fetch up to 100 questions to ensure all are loaded for pagination
      const response = await questionService.getQuestions({ per_page: 100 });

      if (response && response.questions) {
        setQuestions(response.questions);
      } else {
        throw new Error('Failed to load questions');
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      setError(error.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from questions
  const categories = React.useMemo(() => {
    const uniqueCategories = new Set(questions.map(question => question.category));
    return Array.from(uniqueCategories).sort();
  }, [questions]);

  // Filter questions based on search term, type, and category filters
  const filteredQuestions = questions.filter(question => {
    const matchesSearch = question.text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      filter === 'all' ||
      (filter === 'mcq' && question.type === 'mcq') ||
      (filter === 'boolean' && question.type === 'boolean') ||
      (filter === 'text' && question.type === 'text') ||
      (filter === 'range' && question.type === 'range') ||
      (filter === 'color_scale' && question.type === 'color_scale') ||
      (filter === 'linked' && question.protocols && question.protocols.length > 0) ||
      (filter === 'unlinked' && (!question.protocols || question.protocols.length === 0));
    const matchesCategory = categoryFilter === 'all' || question.category === categoryFilter;
    return matchesSearch && matchesType && matchesCategory;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredQuestions.length / QUESTIONS_PER_PAGE);
  const paginatedQuestions = filteredQuestions.slice(
    (currentPage - 1) * QUESTIONS_PER_PAGE,
    currentPage * QUESTIONS_PER_PAGE
  );

  // Reset to first page if filters/search change and current page is out of range
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredQuestions, totalPages]);

  const getProtocolNames = (question) => {
    if (!question.protocols || question.protocols.length === 0) return 'Not linked';
    return question.protocols.map(p => p.name).join(', ');
  };

  const getQuestionChoices = (question) => {
    if (question.type === 'text') {
      return 'Text Answer';
    }
    if (question.type === 'range') {
      return 'Range (1-100)';
    }
    if (question.type === 'color_scale') {
      return 'Red, Orange, Yellow, Green';
    }
    if (!question.choices || question.choices.length === 0) {
      return '-';
    }
    return question.choices.join(', ');
  };

  return (
    <ResearchLayout pageTitle="Manage Questions">
      <div className="container-fluid">
        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage || 'Operation completed successfully.'}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowSuccess(false)}
            ></button>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Header with Actions */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6 mb-3 mb-md-0">
            <h2 className="mb-0">
              <i className="fas fa-question-circle text-primary me-2"></i>
              Research Questions
            </h2>
          </div>
          <div className="col-md-6 text-md-end">
            <Link to="/program-admin/questions/inactive" className="btn btn-outline-warning me-2">
              <i className="fas fa-eye-slash me-2"></i>
              View Inactive Questions
            </Link>
            <Link to="/program-admin/questions" className="btn btn-primary">
              <i className="fas fa-plus me-2"></i>
              Create New Question
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="fas fa-search"></i>
                  </span>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search questions by text..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <select 
                  className="form-select" 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Question Types</option>
                  <option value="mcq">Multiple Choice</option>
                  <option value="boolean">True/False</option>
                  <option value="text">Text</option>
                  <option value="range">Range</option>
                  <option value="color_scale">Color Scale</option>
                  <option value="linked">Linked Questions</option>
                  <option value="unlinked">Unlinked Questions</option>
                </select>
              </div>
              <div className="col-md-3">
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="row">
          <div className="col-12">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading questions...</span>
                </div>
                <p className="mt-2">Loading questions...</p>
              </div>
            ) : filteredQuestions.length > 0 ? (
              <>
                <div className="card border-0 shadow-sm">
                  <div className="table-responsive d-none d-md-block">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Question Text</th>
                          <th>Type</th>
                          <th>Category</th>
                          <th>Options</th>
                          <th>Protocol</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedQuestions.map(question => (
                          <tr key={question.id}>
                            <td>
                              <div style={{ maxWidth: '400px', wordWrap: 'break-word' }}>
                                {question.text}
                              </div>
                            </td>
                            <td>
                              {question.type === 'mcq' && (
                                <span className="badge bg-primary">Multiple Choice</span>
                              )}
                              {question.type === 'boolean' && (
                                <span className="badge bg-info">True/False</span>
                              )}
                              {question.type === 'text' && (
                                <span className="badge bg-success">Text</span>
                              )}
                              {question.type === 'range' && (
                                <span className="badge bg-warning">Range</span>
                              )}
                              {question.type === 'color_scale' && (
                                <span className="badge bg-info">Color</span>
                              )}
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {question.category || 'General'}
                              </span>
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                {getQuestionChoices(question)}
                              </div>
                            </td>
                            <td>
                              {question.protocols && question.protocols.length > 0 ? (
                                <span className="badge bg-success">
                                  {getProtocolNames(question)}
                                </span>
                              ) : (
                                <span className="badge bg-warning text-dark">
                                  Not linked
                                </span>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(question.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div className="btn-group">
                                <Link 
                                  to={`/program-admin/edit-question/${question.id}`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="fas fa-edit"></i> Edit
                                </Link>
                                <Link 
                                  to={`/program-admin/link-questions?question=${question.id}`}
                                  className="btn btn-sm btn-outline-info"
                                >
                                  <i className="fas fa-link"></i> Link
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile Card View */}
                  <div className="d-block d-md-none">
                    {paginatedQuestions.map(question => (
                      <div key={question.id} className="card mb-2 border border-info">
                        <div className="card-body p-2">
                          <div className="mb-1">
                            <strong>Q:</strong> {question.text}
                          </div>
                          <div className="mb-1">
                            <span className="me-2">
                              <strong>Type:</strong> {question.type === 'mcq' ? 'Multiple Choice' : question.type === 'boolean' ? 'True/False' : question.type === 'range' ? 'Range' : question.type === 'color_scale' ? 'Color' : 'Text'}
                            </span>
                            <span>
                              <strong>Category:</strong> {question.category || 'General'}
                            </span>
                          </div>
                          <div className="mb-1">
                            <strong>Options:</strong> {getQuestionChoices(question)}
                          </div>
                          <div className="mb-1">
                            <strong>Protocol:</strong> {question.protocols && question.protocols.length > 0 ? getProtocolNames(question) : 'Not linked'}
                          </div>
                          <div className="mb-1">
                            <strong>Created:</strong> {new Date(question.created_at).toLocaleDateString()}
                          </div>
                          <div className="btn-group mt-1">
                            <Link 
                              to={`/program-admin/edit-question/${question.id}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              <i className="fas fa-edit"></i> Edit
                            </Link>
                            <Link 
                              to={`/program-admin/link-questions?question=${question.id}`}
                              className="btn btn-sm btn-outline-info"
                            >
                              <i className="fas fa-link"></i> Link
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Pagination Controls */}
                <div className="mt-3 d-flex justify-content-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    showInfo={true}
                    totalItems={filteredQuestions.length}
                    itemsPerPage={QUESTIONS_PER_PAGE}
                  />
                </div>
              </>
            ) : (
              <div className="text-center py-5 bg-light rounded">
                <i className="fas fa-question-circle fa-3x text-muted mb-3"></i>
                <h4>No questions found</h4>
                <p className="text-muted">
                  {searchTerm || filter !== 'all' || categoryFilter !== 'all' ? 
                    'No questions match your search criteria.' : 
                    'Get started by creating your first research question.'
                  }
                </p>
                <Link to="/program-admin/questions" className="btn btn-primary mt-2">
                  <i className="fas fa-plus me-2"></i>
                  Create New Question
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Question Count Summary */}
        {filteredQuestions.length > 0 && (
          <div className="mt-4 text-center">
            <div className="badge bg-light text-dark p-2">
              Showing {filteredQuestions.length} of {questions.length} questions
              {filter !== 'all' && (
                <span> ({filter === 'mcq' ? 'multiple choice' : filter === 'boolean' ? 'true/false' : filter} only)</span>
              )}
              {categoryFilter !== 'all' && (
                <span> in {categoryFilter} category</span>
              )}
              {searchTerm && (
                <span> matching "{searchTerm}"</span>
              )}
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminViewQuestions;